import { useMemo } from "react";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { GET_CLIMBER_BY_ID, GET_CLIMBERS_PROGRESS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useProductFeatures } from "./useProductFeatures";
import { extractDistrictId } from "./useClimbersWithProgress";
import type { Climber, ClimberProgress } from "../types/mmp-graphql";

interface ClimberByIdResponse {
  getClimberById: Climber[];
}

interface ClimbersProgressResponse {
  getClimbersProgress: (ClimberProgress | null)[];
}

export interface ClimberWithUserPath extends Omit<Climber, "id" | "path"> {
  userID: string;
  userPath?: string;
  role?: string;
  riasec?: unknown;
  uniqueIdentifiers?: Array<{
    sectionID?: string | null;
    userPath?: string;
  }>;
}

interface QueryKey {
  id: string;
}

const fetchClimberById = async ({
  queryKey,
}: {
  queryKey: [string, QueryKey];
}): Promise<ClimberWithUserPath> => {
  const [, { id }] = queryKey;
  const { getClimberById } = await graphQLClient.request<ClimberByIdResponse>(
    GET_CLIMBER_BY_ID,
    {
      id,
    },
  );
  const climber = getClimberById[0];

  const userObj: ClimberWithUserPath = {
    userID: climber.id,
    sub: climber.sub,
    firstName: climber.firstName,
    lastName: climber.lastName,
    username: climber.username,
    email: climber.email,
    shardID: climber.shardID,
    xp: climber.xp,
    preferredID: climber.preferredID,
    userPath: climber.path,
    receivedSurvey: climber.receivedSurvey,
    // Legacy progress fields off the climber record — still selected by
    // GET_CLIMBER_BY_ID as a fallback for climbers not yet migrated to prg-.
    profileChoices: climber.profileChoices,
    careerPlan: climber.careerPlan,
    coursePlan: climber.coursePlan,
    uniqueIdentifiers: getClimberById.map(({ parentID, path }) => ({
      sectionID: parentID,
      userPath: path,
    })),
  };
  return userObj;
};

// Progress (facets, profileChoices, ladder/career/course plans) now lives on the
// per-product prg- record rather than the climber record, so staff/teacher/admin
// views fetch it separately and merge it onto the climber.
export const useGetClimberById = (
  id: string | undefined,
  enabled: boolean = true,
): UseQueryResult<ClimberWithUserPath, Error> => {
  const { activeProduct } = useProductFeatures();
  const productCode = activeProduct?.code;

  const climberQuery = useQuery({
    queryKey: ["climber", { id: id || "" }],
    queryFn: fetchClimberById,
    enabled: !!id && enabled,
  });

  const climber = climberQuery.data;
  const districtId = extractDistrictId({ path: climber?.userPath });
  const progressEnabled = !!productCode && !!districtId;

  const progressQuery = useQuery<ClimberProgress | null>({
    queryKey: ["climberProgress", productCode, id, districtId],
    queryFn: async () => {
      const resp = await graphQLClient.request<ClimbersProgressResponse>(
        GET_CLIMBERS_PROGRESS,
        {
          climbers: [{ climberId: id, districtId }],
          productCode,
        },
      );
      return resp.getClimbersProgress?.[0] ?? null;
    },
    enabled: progressEnabled,
  });

  const data = useMemo<ClimberWithUserPath | undefined>(() => {
    if (!climber) return climber;
    const progress = progressQuery.data;
    // Prefer the prg- record; fall back to the climber record so legacy
    // climbers whose data hasn't been migrated yet still render.
    return {
      ...climber,
      ladderProgress: progress?.ladderProgress ?? climber.ladderProgress,
      facets: progress?.facets ?? climber.facets,
      profileChoices: progress?.profileChoices ?? climber.profileChoices,
      careerPlan: progress?.careerPlan ?? climber.careerPlan,
      coursePlan: progress?.coursePlan ?? climber.coursePlan,
    };
  }, [climber, progressQuery.data]);

  return {
    ...climberQuery,
    data,
    isLoading:
      climberQuery.isLoading || (progressEnabled && progressQuery.isLoading),
    // Surface progress-fetch failures too, so a prg- error isn't silently
    // hidden behind the (fallback) climber-record data.
    isError: climberQuery.isError || (progressEnabled && progressQuery.isError),
    error: climberQuery.error ?? (progressEnabled ? progressQuery.error : null),
  } as UseQueryResult<ClimberWithUserPath, Error>;
};
